/**
 * Copyright (c) 2026 Farelanes LLC. All rights reserved.
 *
 * Helper: subscription Gate.
 */

/**
 * Subscription gating helpers for the WordPress billing flow.
 * Users without a paid tier are logged out of Auth0 and sent to the shop.
 */

import { releaseAndLogout } from './logout';

export const SHOP_URL = "https://farelanes.com/shop";

export const PAID_SUBSCRIPTION_TYPES = ["CoPilot", "Silver", "Gold", "Platinum"];

/**
 * True when the API subscription payload represents an active paid tier
 * (not missing, not Default).
 */
export function hasActivePaidSubscription(sub) {
  if (!sub) {
    return false;
  }

  const statusIsActive =
    sub.status === true || sub.status === "true" || sub.status === 1;
  const type = sub.subscriptionType;
  const hasPaidType = type && PAID_SUBSCRIPTION_TYPES.includes(type);

  if (statusIsActive && hasPaidType) {
    return true;
  }

  if (Array.isArray(sub.allSubSctiptions) && sub.allSubSctiptions.length > 0) {
    return sub.allSubSctiptions.some((s) => {
      if (!s) {
        return false;
      }
      const sStatusIsActive =
        s.status === true || s.status === "true" || s.status === 1;
      return (
        sStatusIsActive &&
        s.subscriptionType &&
        PAID_SUBSCRIPTION_TYPES.includes(s.subscriptionType)
      );
    });
  }

  return false;
}

/**
 * Poll for a paid tier briefly after signup. WordPress may finish Stripe
 * and call subscription-service a moment after the user lands on the app.
 */
export async function waitForPaidSubscription(
  fetchSubscription,
  { attempts = 4, delayMs = 1500 } = {}
) {
  let last = null;
  for (let i = 0; i < attempts; i++) {
    last = await fetchSubscription();
    if (hasActivePaidSubscription(last)) {
      return last;
    }
    if (i < attempts - 1) {
      await new Promise((resolve) => setTimeout(resolve, delayMs));
    }
  }
  return last;
}

/**
 * End the Auth0 session and send the user to the WordPress shop.
 * Requires https://farelanes.com/shop (or https://farelanes.com) in Auth0 Allowed Logout URLs.
 */
export function logoutToShop(logout) {
  releaseAndLogout(logout, SHOP_URL);
}
